import { AlertTriangle } from 'lucide-react';

import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { AUTH_ERROR_MESSAGES } from '@/lib/auth-errors';

/**
 * Top-of-page banner shown only when the session was superseded by another
 * device. The toast in useSessionGuard fires once on transition; this
 * banner stays visible until the user signs in again or dismisses it
 * via the explicit sign-in CTA.
 *
 * Mounted globally inside the PageShell.
 */
export function SessionGuardBanner({ onSignInClick }: { onSignInClick?: () => void }) {
  const { status, clearLocal } = useAuth();

  if (status !== 'superseded') return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="relative z-20 border-b border-vermilion/40 bg-vermilion/10"
    >
      <div className="mx-auto flex max-w-7xl items-start gap-3 px-6 py-3">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-vermilion" aria-hidden />
        <div className="flex-1">
          <p className="text-sm font-medium text-ink">
            Your session ended.
          </p>
          <p className="text-xs text-graphite">
            {AUTH_ERROR_MESSAGES.SESSION_SUPERSEDED}
          </p>
        </div>
        <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
          {onSignInClick ? (
            <Button variant="outline" size="sm" onClick={onSignInClick}>
              Sign in again
            </Button>
          ) : null}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              void clearLocal();
            }}
          >
            Sign out
          </Button>
        </div>
      </div>
    </div>
  );
}
